"use client";

import type React from "react";
import { createContext, useState, useContext, useEffect, useMemo } from "react";

/** 실제로 화면에 칠해지는 두 가지. */
export type Theme = "light" | "dark";
/**
 * 사용자가 «고른» 값. `auto` 는 "안 골랐다" = 폰·PC 의 낮/밤 설정을 그대로 따른다는 뜻이다.
 * 기본값이 이것이라 처음 쓰는 사람은 자기 기기 설정대로 보게 된다.
 */
export type ThemePreference = Theme | "auto";

/**
 * ⚠️ **저장 키가 새것이다** (2026-09-09).
 *
 * 옛 코드는 앱을 열 때마다 `theme` 에 현재 값을 **자동으로 써 버렸다.** 그래서 한 번이라도
 * 앱을 연 사람은 저장소에 "라이트를 골랐다"는 기록이 남아 있고, 기본값만 `auto` 로 바꿔서는
 * **아무에게도 오토가 적용되지 않는다** — 고르지도 않은 값이 선택으로 굳어 있기 때문이다.
 * 그래서 «고른 값»만 담는 키를 새로 파고, 옛 키는 읽지 않고 지운다(한 번만 일어나는 이관).
 * 일부러 나이트로 두고 쓰던 사람은 이 이관에서 한 번 오토로 돌아가지만, 상단 띠의 토글
 * 한 번이면 되돌아가고 그 뒤로는 그 선택이 유지된다.
 */
const STORAGE_KEY = "theme_pref";
const LEGACY_KEY = "theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * 고객이 링크로 여는 화면 — 기기·저장 설정과 상관없이 **늘 라이트**로 칠한다(2026-10-10).
 *
 * 이 화면들은 흰 바탕으로만 만들어져 있는데, 입력칸 같은 공용 부품은 나이트에서 글자를
 * 흰색으로 바꾼다. 기본값이 「기기 설정」이 된 뒤(2026-09-09) 폰을 다크 모드로 쓰는 고객에게
 * 신청서 글자가 흰 바탕 위 흰 글씨로 보였다(슬랙 #기능추가및개선 10/10 신고).
 * 나이트를 제대로 지원하는 화면(로그인·서명 패드·문자 앱 안내)은 넣지 않는다.
 *
 * 이 화면들은 전부 링크로 «새로 열려» 들어오므로(앱 안에서 넘어오는 길 없음) 처음 열 때
 * 주소만 보면 된다. ⚠️ index.html 의 첫 칠하기 스크립트에 같은 목록이 있다 — 함께 고친다.
 */
const LIGHT_ONLY_PATH_PREFIXES = [
  "/form/",
  "/form-simple/",
  "/apply",
  "/policy-upload/",
  "/payback/",
  "/payback-apply/",
  "/quote/",
  "/rebate/",
  "/f/",
  "/talk/",
] as const;

function isLightOnlyPage(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return LIGHT_ONLY_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

type ThemeContextType = {
  /** 지금 실제로 칠해진 테마. */
  theme: Theme;
  /** 그 테마가 어디서 왔는지 — 기기 설정(`auto`)인지 사용자가 고른 값인지. */
  preference: ThemePreference;
  /** 라이트 ↔ 나이트를 뒤집는다(상단 띠의 아이콘 하나짜리 자리용). 누르면 «고른 값»이 된다. */
  toggleTheme: () => void;
  /** 곧바로 이 값으로 정한다 — 세 칸을 다 펴 보이는 설정 화면용. */
  chooseTheme: (next: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function savedPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // 저장이 막힌 브라우저(사파리 프라이빗 등)에서는 기기 설정을 따른다.
  }
  return "auto";
}

function systemTheme(): Theme {
  return typeof matchMedia === "function" && matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preference, setPreference] = useState<ThemePreference>("auto");
  const [deviceTheme, setDeviceTheme] = useState<Theme>("light");
  const [isInitialized, setIsInitialized] = useState(false);
  const [lightOnly] = useState(isLightOnlyPage);
  const theme: Theme = lightOnly ? "light" : preference === "auto" ? deviceTheme : preference;

  // 저장된 «고른 값»과 기기 설정을 브라우저에서만 읽는다(SSR 안전).
  useEffect(() => {
    setPreference(savedPreference());
    setDeviceTheme(systemTheme());
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      // 지우지 못해도 새 키만 읽으므로 동작에는 영향이 없다.
    }
    setIsInitialized(true);
  }, []);

  // 기기 설정을 따르는 동안에는 OS 의 낮/밤 전환이 새로고침 없이 바로 반영돼야 한다.
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const query = matchMedia(DARK_QUERY);
    const sync = (event: MediaQueryListEvent) => setDeviceTheme(event.matches ? "dark" : "light");
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
  }, [theme, isInitialized]);

  // ⚠️ **고른 값만 저장한다** — 기기 설정을 따르는 상태까지 저장하면 그게 곧 "직접 고른 값"이
  //    돼 버려, 옛 코드가 만들었던 문제(오토가 영영 안 먹히는 상태)를 그대로 되만든다.
  useEffect(() => {
    if (!isInitialized) return;
    try {
      if (preference === "auto") localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // 저장이 막힌 브라우저에서도 이 탭의 전환은 그대로 쓸 수 있다.
    }
  }, [preference, isInitialized]);

  const value = useMemo(
    () => ({
      theme,
      preference,
      // 지금 보이는 화면의 반대로 «정한다» — 오토 상태에서 눌러도 뜻대로 뒤집힌다.
      toggleTheme: () => setPreference(theme === "dark" ? "light" : "dark"),
      chooseTheme: (next: ThemePreference) => setPreference(next),
    }),
    [theme, preference],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
